import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";

import { DocumentAccessLogsService } from "../../../api/services";
import {
  LogDocumentAccessDto,
  DocumentAccessLogResponseDto,
  AccessStatsResponseDto,
  LogAccessResponseDto,
} from "../../../api/models";
import { environment } from "../../../environments/environment";
import { 
  AccessLogQueryDto, 
  AccessLogStatsQueryDto, 
  AuditTrailQueryDto 
} from "../../../interfaces/documents";

const TENANT = environment.tenant;

@Injectable({ providedIn: "root" })
export class DocumentAccessLogsApiService {
  private readonly _accessLogsService: DocumentAccessLogsService = inject(
    DocumentAccessLogsService,
  );

  /**
   * Log document view access
   */
  protected _logView$(
    documentId: string,
    logData?: Partial<LogDocumentAccessDto>,
  ): Observable<LogAccessResponseDto> {
    const body: LogDocumentAccessDto = {
      ...logData,
    };
    return this._accessLogsService.documentAccessLogControllerLogDocumentView({
      tenant: TENANT,
      documentId,
      body,
    });
  }

  /**
   * Log document download access
   */
  protected _logDownloadAccess$(
    documentId: string,
    logData?: Partial<LogDocumentAccessDto>,
  ): Observable<LogAccessResponseDto> {
    const body: LogDocumentAccessDto = {
      ...logData,
    };
    return this._accessLogsService.documentAccessLogControllerLogDocumentDownload(
      {
        tenant: TENANT,
        documentId,
        body,
      },
    );
  }

  /**
   * Get access logs for a document
   */
  protected _getDocumentLogs$(
    documentId: string,
    query?: AccessLogQueryDto,
  ): Observable<DocumentAccessLogResponseDto[]> {
    return this._accessLogsService.documentAccessLogControllerGetDocumentAccessLogs(
      {
        tenant: TENANT,
        documentId,
        limit: query?.limit,
        startTime: query?.startTime,
        endTime: query?.endTime,
      },
    );
  }

  /**
   * Get access logs for a patient
   */
  protected _getPatientLogs$(
    patientId: string,
    query?: AccessLogQueryDto,
  ): Observable<DocumentAccessLogResponseDto[]> {
    return this._accessLogsService.documentAccessLogControllerGetPatientAccessLogs(
      {
        tenant: TENANT,
        patientId,
        limit: query?.limit,
        startTime: query?.startTime,
        endTime: query?.endTime,
      },
    );
  }

  /**
   * Get access logs for a user
   */
  protected _getUserLogs$(
    userId: string,
    query?: AccessLogQueryDto,
  ): Observable<DocumentAccessLogResponseDto[]> {
    return this._accessLogsService.documentAccessLogControllerGetUserAccessLogs(
      {
        tenant: TENANT,
        userId,
        limit: query?.limit,
        startTime: query?.startTime,
        endTime: query?.endTime,
      },
    );
  }

  /**
   * Get access logs for a user role
   */
  protected _getUserRoleLogs$(
    userRole: string,
    query?: AccessLogQueryDto,
  ): Observable<DocumentAccessLogResponseDto[]> {
    return this._accessLogsService.documentAccessLogControllerGetUserRoleAccessLogs(
      {
        tenant: TENANT,
        userRole,
        limit: query?.limit,
        startTime: query?.startTime,
        endTime: query?.endTime,
      },
    );
  }

  /**
   * Get recent access logs
   */
  protected _getRecentLogs$(query?: AccessLogQueryDto): Observable<DocumentAccessLogResponseDto[]> {
    return this._accessLogsService.documentAccessLogControllerGetRecentAccessLogs(
      {
        tenant: TENANT,
        limit: query?.limit,
        startTime: query?.startTime,
        endTime: query?.endTime,
      },
    );
  }

  /**
   * Get access statistics
   */
  protected _getAccessStats$(query?: AccessLogStatsQueryDto): Observable<AccessStatsResponseDto> {
    return this._accessLogsService.documentAccessLogControllerGetAccessStats({
      tenant: TENANT,
      startTime: query?.startTime,
      endTime: query?.endTime,
    });
  }

  /**
   * Get audit trail for compliance
   */
  protected _getAuditTrail$(query: AuditTrailQueryDto): Observable<DocumentAccessLogResponseDto[]> {
    return this._accessLogsService.documentAccessLogControllerGetAuditTrail({
      tenant: TENANT,
      startDate: query.startDate,
      endDate: query.endDate,
      documentId: query.documentId,
      patientId: query.patientId,
      userId: query.userId,
    });
  }
}